import * as z from "zod";
import { cursorPageOf } from "../index";
import { type EmailBlockType, emailLocale } from "./email-document";
import { bounceTypes, deliveryStates, sendLinkKinds, trackingLevels } from "./engagement/enums";

// The tabs of the report of a send after its overview (ADR-315 §6, plan §9.1): where they clicked, on the email as the
// send drew it, with the rate of each block on who saw it; every link; the buyers with their filters; the arrivals by
// the domain of the address. Counted by crm in its summaries, never from the events; the people only in Buyers, which
// a delegated access never reads (ADR-300 §4).

const sendId = z.uuid();
const count = z.number().int();
const instant = z.iso.datetime();

/** The sizes of the tabs: a domain shows its numbers only with this many people (`arrivalsMinGroup`). */
export const SEND_CLICKS = {
  arrivalsMinGroup: 5,
  /** The domains listed by name, the most people first; the others are counted together. */
  arrivalsMaxGroups: 20,
  buyersPage: 50,
  buyersMaxPage: 200,
} as const;

/** The kinds of block of an email, as the click map names them. */
export const emailBlockTypes = [
  "heading",
  "text",
  "image",
  "button",
  "booking_button",
  "product_grid",
  "logo_strip",
  "event_card",
  "divider",
  "spacer",
] as const satisfies readonly EmailBlockType[];
// every kind of the document is a kind of the map: a new block type fails here until it is listed
const _everyBlockType: Exclude<EmailBlockType, (typeof emailBlockTypes)[number]> extends never ? true : never = true;
void _everyBlockType;

/** Who the Buyers tab shows (plan §9.1): everyone the email went to, or one of the lists to act on. */
export const sendBuyerFilters = ["all", "clicked_not_booked", "stopped_at_times", "not_delivered"] as const;
export type SendBuyerFilter = (typeof sendBuyerFilters)[number];

export const getCrmSendPreviewInput = z.object({
  sendId,
  /** The language of the email; the default one of the send without. */
  locale: emailLocale.optional(),
});
export type GetCrmSendPreviewInput = z.input<typeof getCrmSendPreviewInput>;

/**
 * The email as the send drew it (the version it photographed), every block whatever `visibleIf`, for a sample
 * recipient: never a real contact. With the marks of sections, blocks, items and links (`data-nx-*`) the click map
 * lays its numbers on; it goes only into the frame that runs nothing (Q-SEC-42 point 2).
 */
export const crmSendPreviewDto = z
  .object({ locale: emailLocale, subject: z.string(), html: z.string() })
  .meta({ id: "CrmSendPreview" });
export type CrmSendPreviewDto = z.output<typeof crmSendPreviewDto>;

export const getCrmSendClicksInput = z.object({
  sendId,
  /** The language of the blocks; the one most people got without. */
  locale: emailLocale.optional(),
});
export type GetCrmSendClicksInput = z.input<typeof getCrmSendClicksInput>;

/**
 * The clicks of a link, an item or a block: of real people and of everything recorded, and the people who clicked
 * (only those whose level of measure keeps the person, ADR-314 §1).
 */
const clicks = {
  clicksHuman: count,
  clicksAll: count,
  uniqueHuman: count,
  uniqueAll: count,
};

/** Who got the block: everyone, and those whose level keeps the person, the base of the rate of unique clicks. */
const exposure = { exposed: count, exposedNamed: count };

export const sendClickItemDto = z.object({ itemId: z.string(), words: z.string(), ...clicks });
export type SendClickItemDto = z.output<typeof sendClickItemDto>;

/** A block of the email of one language, in the order of the document. */
export const sendClickBlockDto = z
  .object({
    blockId: z.string(),
    type: z.enum(emailBlockTypes),
    /** A few words of it: its first line, its label, its alt text, its title. */
    words: z.string(),
    ...exposure,
    ...clicks,
    /** The booking button: the people who opened their booking link (it never goes through the redirect, ADR-313 §5). */
    linkOpened: count.nullable(),
    /** The products of a grid, the logos of a strip. */
    items: z.array(sendClickItemDto),
  })
  .meta({ id: "SendClickBlock" });
export type SendClickBlockDto = z.output<typeof sendClickBlockDto>;

/** A link of the send in one language (`crm.send_link`), with who got its block. */
export const sendLinkStatDto = z
  .object({
    linkIdx: count,
    locale: emailLocale,
    /** `blockId[:itemId]:n`: the `data-nx-link` of the link in the email of the preview. */
    key: z.string().nullable(),
    blockId: z.string(),
    itemId: z.string().nullable(),
    kind: z.enum(sendLinkKinds),
    label: z.string().nullable(),
    /** Where it leads; null for the booking button, which is each buyer's own. */
    url: z.string().nullable(),
    ...clicks,
    ...exposure,
    /** The booking button: the people of the language who opened their booking link. */
    linkOpened: count.nullable(),
  })
  .meta({ id: "SendLinkStat" });
export type SendLinkStatDto = z.output<typeof sendLinkStatDto>;

/** A language of the send: how many got it, and how many of them opened their booking link. */
export const sendLocaleDto = z.object({ locale: emailLocale, sent: count, linkOpened: count });
export type SendLocaleDto = z.output<typeof sendLocaleDto>;

/** Where they clicked and every link (ADR-315 §6): the blocks of one language, the links of all of them. */
export const crmSendClicksDto = z
  .object({
    /** The language of `blocks`. */
    locale: emailLocale,
    /** The languages the email went out in, the most people first. */
    locales: z.array(sendLocaleDto),
    blocks: z.array(sendClickBlockDto),
    links: z.array(sendLinkStatDto),
  })
  .meta({ id: "CrmSendClicks" });
export type CrmSendClicksDto = z.output<typeof crmSendClicksDto>;

export const listCrmSendBuyersInput = z.object({
  sendId,
  filter: z.enum(sendBuyerFilters).default("all"),
  /** Opaque: the account and the name of the last person of the page before (a keyset, 50-api/README §4). */
  cursor: z.string().min(1).max(2000).optional(),
  limit: z.coerce.number().int().min(1).max(SEND_CLICKS.buyersMaxPage).default(SEND_CLICKS.buyersPage),
});
export type ListCrmSendBuyersInput = z.input<typeof listCrmSendBuyersInput>;

/** A person the email went to, and how far they went. */
export const sendBuyerDto = z
  .object({
    contactPersonId: z.uuid(),
    name: z.string(),
    accountOrgId: z.uuid(),
    accountName: z.string(),
    locale: emailLocale,
    /** What the send measured of them (ADR-314 §1). */
    measure: z.enum(trackingLevels),
    delivery: z.enum(deliveryStates),
    bounceType: z.enum(bounceTypes).nullable(),
    /** The provider did not take the email. */
    failed: z.boolean(),
    /** Null where opens are not measured (any level but `full`). */
    opened: z.boolean().nullable(),
    /** Their clicks of real people; null where they are counted without a name (`aggregate`). */
    clicks: count.nullable(),
    lastSignalAt: instant.nullable(),
    linkOpenedAt: instant.nullable(),
    slotsViewedAt: instant.nullable(),
    bookedAt: instant.nullable(),
    unsubscribedAt: instant.nullable(),
    complainedAt: instant.nullable(),
  })
  .meta({ id: "SendBuyer" });
export type SendBuyerDto = z.output<typeof sendBuyerDto>;

export const crmSendBuyerPageDto = cursorPageOf(sendBuyerDto).meta({ id: "CrmSendBuyerPage" });
export type CrmSendBuyerPageDto = z.output<typeof crmSendBuyerPageDto>;

export const getCrmSendArrivalsInput = z.object({ sendId });
export type GetCrmSendArrivalsInput = z.input<typeof getCrmSendArrivalsInput>;

/** The people of one domain of address, or of the domains too small to name (`domain` null), and what reached them. */
export const sendArrivalDto = z
  .object({
    domain: z.string().nullable(),
    recipients: count,
    delivered: count,
    /** Hard bounces, refusals of the provider and emails it did not take: as the tile "Not delivered". */
    notDelivered: count,
    complained: count,
    /** At the level `full`: whose opens are measured. */
    measured: count,
    opened: count,
    /** Whose clicks are recorded by person. */
    named: count,
    clicked: count,
  })
  .meta({ id: "SendArrival" });
export type SendArrivalDto = z.output<typeof sendArrivalDto>;

/**
 * The arrivals by the domain of the address (ADR-315 §6): a domain is named with at least `minGroup` people, the others
 * counted together; when even those are fewer, only how many (`withheld`), so no small group tells who it is.
 */
export const crmSendArrivalsDto = z
  .object({
    minGroup: count,
    groups: z.array(sendArrivalDto),
    withheld: count,
  })
  .meta({ id: "CrmSendArrivals" });
export type CrmSendArrivalsDto = z.output<typeof crmSendArrivalsDto>;
